import React, { createContext, useContext, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import './index.css';

// Layout Components
import { AppLayout } from './components/Layout';

// Pages
import EventDashboard from './pages/rms/EventDashboard';
import SeriesSelection from './pages/rms/SeriesSelection';
import SeasonDashboard from './pages/rms/SeasonDashboard';
import EventsList from './pages/rms/EventsList';
import EventDetails from './pages/rms/EventDetails';
import EventEntries from './pages/rms/EventEntries';
import CompetitorsList from './pages/rms/CompetitorsList';
import CompetitorDetails from './pages/rms/CompetitorDetails';
import EventForm from './pages/rms/EventForm';
import CompetitorForm from './pages/rms/CompetitorForm';
import SeasonConfig from './pages/rms/SeasonConfig';
import PointsConfig from './pages/rms/PointsConfig';
import ClassesConfig from './pages/rms/ClassesConfig';
import EventResults from './pages/rms/EventResults';
import PaymentsConfig from './pages/rms/PaymentsConfig';
import PointsStandings from './pages/rms/PointsStandings';
import Memberships from './pages/rms/Memberships';
import Suspensions from './pages/rms/Suspensions';
import Reports from './pages/rms/Reports';
import Settings from './pages/rms/Settings';
import Tools from './pages/rms/Tools';
import LineupMonitor from './pages/rms/LineupMonitor';
import AnnouncerScreen from './pages/rms/AnnouncerScreen';
import TransponderManagement from './pages/rms/TransponderManagement';
import Results from './pages/rms/Results';
import SeriesConfig from './pages/rms/SeriesConfig';
import FantasyConfig from './pages/rms/FantasyConfig';
import EventSetup from './pages/rms/EventSetup';
import RaceLineup from './pages/rms/RaceLineup';

// Global Pages (not series-specific)
import Login from './pages/Login';

// Auth Components
import ProtectedRoute from './components/ProtectedRoute';

// Context Providers (keeping for API access)
import { AuthProvider } from './contexts/AuthContext';
import { ApiProvider } from './contexts/ApiContext';
import { SocketProvider } from './contexts/SocketContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Series Context for sharing selected series across the app
const SeriesContext = createContext(null);

export function useSeries() {
  return useContext(SeriesContext);
}

// Series data mapped to database wrg_series_ID values
// These IDs come from the wrg_series table in the database
const seriesData = {
  'woo-sprint': {
    id: 'woo-sprint',
    dbId: 8, // wrg_series_ID for World of Outlaws Sprint Car Series
    name: 'World of Outlaws Sprint Cars',
    shortName: 'WoO Sprint',
    slug: 'woo-sprint',
    color: '#0c7abf',
    logo: 'https://worldofoutlaws.com/wp-content/uploads/2022/11/NOS_SCS_LOGO_FINAL_RGB-e1703214389728.png',
  },
  'world-of-outlaws-late-model-series': {
    id: 'world-of-outlaws-late-model-series',
    dbId: 7, // wrg_series_ID for World of Outlaws Late Model Series
    name: 'World of Outlaws Late Models',
    shortName: 'WoO Late Model',
    slug: 'world-of-outlaws-late-model-series',
    color: '#2e449c',
    logo: 'https://dv51o5gtpfm3w.cloudfront.net/2026-WoO-LM.png',
  },
  'super-dirtcar': {
    id: 'super-dirtcar',
    dbId: 3, // wrg_series_ID for Super DIRTcar Series
    name: 'Super DIRTcar Series',
    shortName: 'SDS',
    slug: 'super-dirtcar',
    color: '#EB1C25',
    logo: 'https://cdn.dirtcar.com/wp-content/uploads/sites/2/2019/10/30120141/SDCS_Logo.png',
  },
  'xtreme-midget': {
    id: 'xtreme-midget',
    dbId: 12, // wrg_series_ID for Xtreme Outlaw Midget Series
    name: 'Xtreme Outlaw Midget Series',
    shortName: 'XO Midget',
    slug: 'xtreme-midget',
    color: '#C12B2A',
    logo: null,
  },
  'ascs': {
    id: 'ascs',
    dbId: 21, // wrg_series_ID for ASCS National Series
    name: 'ASCS National Series',
    shortName: 'ASCS',
    slug: 'ascs',
    color: '#8B0000',
    logo: 'https://cdn.dirtcar.com/wp-content/uploads/2024/04/30175124/ASCSNational.png',
  },
  'dirtcar-summer': {
    id: 'dirtcar-summer',
    dbId: 2, // wrg_series_ID for DIRTcar Summer Nationals
    name: 'DIRTcar Summer Nationals',
    shortName: 'Summer Nats',
    slug: 'dirtcar-summer',
    color: '#F15A22',
    logo: 'https://cdn.dirtcar.com/wp-content/uploads/sites/19/2019/07/30130712/SUMMER_NATS_FINAL-4.png',
  },
  'summit-mods': {
    id: 'summit-mods',
    dbId: 1, // wrg_series_ID for Summit Modified Nationals
    name: 'Summit Modified Nationals',
    shortName: 'Summit Mods',
    slug: 'summit-mods',
    color: '#233279',
    logo: 'https://cdn.dirtcar.com/wp-content/uploads/sites/19/2021/01/30130249/2021_SUMMIT_MODS_LOGO-J1671_small.png',
  },
  'dirtcar-358': {
    id: 'dirtcar-358',
    dbId: 11, // wrg_series_ID for DIRTcar 358 Modified Series
    name: 'DIRTcar 358 Modified Series',
    shortName: '358 Mods',
    slug: 'dirtcar-358',
    color: '#FF6600',
    logo: 'http://www.dirtcarump.com/wp-content/uploads/2020/01/2019_DIRTCAR_358_SERIES_LOGO.png',
  },
  'mars-latemodel': {
    id: 'mars-latemodel',
    dbId: 6, // wrg_series_ID for MARS Late Model Championship
    name: 'MARS Late Model Championship',
    shortName: 'MARS LM',
    slug: 'mars-latemodel',
    color: '#4A90A4',
    logo: 'https://cdn.dirtcar.com/wp-content/uploads/2024/05/02162656/MARS-LM-Logo_web.png',
  },
  'midgets': {
    id: 'midgets',
    racingEntityId: 172, // racing_entity_ID for Midgets
    name: 'Midgets',
    shortName: 'Midgets',
    slug: 'midgets',
    color: '#FF5500',
    logo: null,
  },
};

// Inner component that can use hooks from context
function SeriesLayoutInner() {
  const { seriesSlug } = useParams();

  const { data: currentSeries, isLoading } = useQuery({
    queryKey: ['series', seriesSlug],
    queryFn: async () => {
      if (seriesData[seriesSlug]) {
        return seriesData[seriesSlug];
      }

      const response = await fetch('/api/home/dashboard');
      const data = await response.json();
      if (data.success && data.series) {
        const series = data.series.find(s => s.slug === seriesSlug);
        if (series) {
          return {
            id: series.slug,
            dbId: series.id,
            name: series.name,
            shortName: series.name,
            slug: series.slug,
            color: series.color ? `#${series.color}` : '#64748b',
            logo: series.logo_URL,
          };
        }
      }
      return null;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wrg-blue-600"></div>
      </div>
    );
  }

  if (!currentSeries) {
    return <Navigate to="/" replace />;
  }

  return (
    <SeriesContext.Provider value={currentSeries}>
      <AppLayout currentSeries={currentSeries}>
        <Outlet />
      </AppLayout>
    </SeriesContext.Provider>
  );
}

// Wrapper component for series-specific routes
function SeriesLayout() {
  return <SeriesLayoutInner />;
}

// Placeholder components for routes not yet built
function PlaceholderPage({ title }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
      <div className="w-16 h-16 bg-rms-dark-700 rounded-lg flex items-center justify-center mb-4">
        <span className="text-3xl">🚧</span>
      </div>
      <h2 className="text-xl font-semibold text-slate-100 mb-2">{title}</h2>
      <p className="text-slate-400">This module is under construction</p>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ApiProvider>
        <SocketProvider>
          <Router>
          <Routes>
            {/* Public route - Login */}
            <Route path="/login" element={<Login />} />

            {/* Protected routes - require authentication */}
            <Route path="/" element={<ProtectedRoute><EventDashboard /></ProtectedRoute>} />
            <Route path="/series" element={<ProtectedRoute><SeriesSelection /></ProtectedRoute>} />


            {/* Series-specific routes */}
            <Route path="/:seriesSlug" element={<ProtectedRoute><SeriesLayout /></ProtectedRoute>}>
              {/* Season Dashboard */}
              <Route path="season" element={<SeasonDashboard />} />

              {/* Configuration */}
              <Route path="config/season" element={<SeasonConfig />} />
              <Route path="config/series" element={<SeriesConfig />} />
              <Route path="config/classes" element={<ClassesConfig />} />
              <Route path="config/points" element={<PointsConfig />} />
              <Route path="config/payments" element={<PaymentsConfig />} />
              <Route path="config/fantasy" element={<FantasyConfig />} />

              {/* Events */}
              <Route path="events" element={<EventsList />} />
              <Route path="events/new" element={<EventForm />} />
              <Route path="events/:eventId" element={<EventDetails />} />
              <Route path="events/:eventId/edit" element={<EventForm />} />
              <Route path="events/:eventId/setup" element={<EventSetup />} />
              <Route path="events/:eventId/entries" element={<EventEntries />} />
              <Route path="events/:eventId/results" element={<EventResults />} />

              {/* Races */}
              <Route path="races/:raceId/lineup" element={<RaceLineup />} />

              {/* Competitors */}
              <Route path="competitors" element={<CompetitorsList />} />
              <Route path="competitors/new" element={<CompetitorForm />} />
              <Route path="competitors/:competitorId" element={<CompetitorDetails />} />
              <Route path="competitors/:competitorId/edit" element={<CompetitorForm />} />

              {/* Points */}
              <Route path="points" element={<PointsStandings />} />

              {/* Results */}
              <Route path="results" element={<Results />} />

              {/* Memberships */}
              <Route path="memberships" element={<Memberships />} />

              {/* Suspensions */}
              <Route path="suspensions" element={<Suspensions />} />

              {/* Reports */}
              <Route path="reports" element={<Reports />} />
              <Route path="reports/:reportType" element={<Reports />} />

              {/* Tools */}
              <Route path="tools" element={<Tools />} />
              <Route path="tools/lineup-monitor" element={<LineupMonitor />} />
              <Route path="tools/announcer" element={<AnnouncerScreen />} />
              <Route path="tools/transponders" element={<TransponderManagement />} />

              {/* Settings */}
              <Route path="settings" element={<Settings />} />

              {/* Default redirect for series root */}
              <Route index element={<Navigate to="season" replace />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={
              <div className="min-h-screen bg-rms-dark-900 flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-slate-100 mb-2">404</h1>
                  <p className="text-slate-400 mb-4">Page not found</p>
                  <a href="/" className="btn-primary">Return Home</a>
                </div>
              </div>
            } />
          </Routes>
          </Router>
        </SocketProvider>
      </ApiProvider>
    </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
